import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FiClock, FiEye, FiSearch, FiChevronRight, FiPlus } from 'react-icons/fi';
import { blogAPI } from '../services/blogAPI';
import { useAuth } from '../context/AuthContext';
import SEO, { PageSEO } from '../components/SEO';

const ALL_CATEGORIES = [
  { name: 'ALL', displayName: 'All Posts' },
  { name: 'WEIGHT_LOSS_TIPS', displayName: 'Weight Loss Tips' },
  { name: 'HEALTHY_RECIPES', displayName: 'Healthy Recipes' },
  { name: 'NUTRITION', displayName: 'Nutrition' },
  { name: 'EXERCISE', displayName: 'Exercise' },
  { name: 'GLP1_MEDICATION', displayName: 'GLP-1 Medication' },
  { name: 'MENTAL_HEALTH', displayName: 'Mental Health' },
  { name: 'SUCCESS_STORIES', displayName: 'Success Stories' },
  { name: 'LIFESTYLE', displayName: 'Lifestyle' },
  { name: 'SLEEP_WELLNESS', displayName: 'Sleep & Wellness' },
  { name: 'METABOLISM', displayName: 'Metabolism' },
];

const Blog = ({ glp1Mode = false }) => {
  const [blogs, setBlogs] = useState([]);
  const [featuredBlogs, setFeaturedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [categoryCounts, setCategoryCounts] = useState({});
  const { user } = useAuth();

  const activeCategory = searchParams.get('category') || 'ALL';

  // On GLP-1 site: only show GLP1_MEDICATION category filter
  // On main site: hide GLP1_MEDICATION from filter
  const categories = glp1Mode
    ? [
        { name: 'ALL', displayName: 'All GLP-1 Articles' },
        { name: 'GLP1_MEDICATION', displayName: 'GLP-1 Medication' },
      ]
    : ALL_CATEGORIES.filter(c => c.name !== 'GLP1_MEDICATION');

  useEffect(() => {
    fetchBlogs();
    fetchFeaturedBlogs();
    fetchCategoryCounts();
  }, [activeCategory, glp1Mode]);

  // Wrap any promise with a 15-second timeout to prevent infinite spinner
  const withTimeout = (promise, ms = 15000) =>
    Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Please check your connection.')), ms)
      ),
    ]);

  const fetchBlogs = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      let response;
      if (glp1Mode) {
        // GLP-1 site: always show only GLP1_MEDICATION articles
        response = await withTimeout(blogAPI.getBlogsByCategory('GLP1_MEDICATION'));
      } else if (activeCategory === 'ALL') {
        response = await withTimeout(blogAPI.getAllBlogs());
        // Main site: hide GLP1_MEDICATION articles
        response = { data: response.data.filter(b => b.category !== 'GLP1_MEDICATION') };
      } else {
        response = await withTimeout(blogAPI.getBlogsByCategory(activeCategory));
      }
      setBlogs(response.data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setFetchError(error.message || 'Failed to load articles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedBlogs = async () => {
    try {
      const response = await blogAPI.getFeaturedBlogs();
      let featured = response.data;
      if (glp1Mode) {
        featured = featured.filter(b => b.category === 'GLP1_MEDICATION');
      } else {
        featured = featured.filter(b => b.category !== 'GLP1_MEDICATION');
      }
      setFeaturedBlogs(featured.slice(0, 3));
    } catch (error) {
      console.error('Error fetching featured blogs:', error);
    }
  };

  const fetchCategoryCounts = async () => {
    try {
      const response = await blogAPI.getCategoryCounts();
      setCategoryCounts(response.data);
    } catch (error) {
      console.error('Error fetching category counts:', error);
    }
  };

  const handleCategoryChange = (category) => {
    if (category === 'ALL') {
      setSearchParams({});
    } else {
      setSearchParams({ category });
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      fetchBlogs();
      return;
    }
    setLoading(true);
    try {
      const response = await blogAPI.searchBlogs(searchQuery);
      let results = response.data;
      if (glp1Mode) {
        results = results.filter(b => b.category === 'GLP1_MEDICATION');
      } else {
        results = results.filter(b => b.category !== 'GLP1_MEDICATION');
      }
      setBlogs(results);
    } catch (error) {
      console.error('Error searching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      WEIGHT_LOSS_TIPS: 'bg-orange-100 text-orange-700',
      HEALTHY_RECIPES: 'bg-green-100 text-green-700',
      NUTRITION: 'bg-blue-100 text-blue-700',
      EXERCISE: 'bg-red-100 text-red-700',
      GLP1_MEDICATION: 'bg-purple-100 text-purple-700',
      MENTAL_HEALTH: 'bg-pink-100 text-pink-700',
      SUCCESS_STORIES: 'bg-yellow-100 text-yellow-700',
      LIFESTYLE: 'bg-teal-100 text-teal-700',
      SLEEP_WELLNESS: 'bg-indigo-100 text-indigo-700',
      METABOLISM: 'bg-cyan-100 text-cyan-700',
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  const heroGradient = glp1Mode
    ? 'bg-gradient-to-r from-navy-900 to-navy-800'
    : 'bg-gradient-to-r from-sky-600 to-sky-800';

  const heroBadgeColor = glp1Mode ? 'text-gold-300' : 'text-sky-100';
  const filterActiveColor = glp1Mode ? 'bg-gold-500 text-white shadow-md' : 'bg-sky-600 text-white shadow-md';
  const readMoreColor = glp1Mode ? 'text-gold-600 hover:text-gold-700' : 'text-sky-600 hover:text-sky-700';
  const newsletterGradient = glp1Mode
    ? 'bg-gradient-to-r from-navy-900 to-navy-800'
    : 'bg-gradient-to-r from-sky-600 to-sky-800';

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO {...PageSEO.blog} />

      {/* Hero Section */}
      <section className={`${heroGradient} text-white py-16`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {glp1Mode && (
              <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-1.5 rounded-full mb-4 text-sm font-medium text-gold-300">
                GLP-1 Medication Insights
              </div>
            )}
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {glp1Mode ? 'GLP-1 Health Blog' : 'Fitnession Blog'}
            </h1>
            <p className={`text-xl ${heroBadgeColor} max-w-2xl mx-auto mb-8`}>
              {glp1Mode
                ? 'Expert insights on GLP-1 medications, medical weight loss, and what to expect on your journey.'
                : 'Expert insights on weight loss, nutrition, fitness, and living your healthiest life.'}
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-xl mx-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-6 py-4 rounded-full text-gray-800 focus:outline-none focus:ring-2 focus:ring-white/50 shadow-lg"
                />
                <button
                  type="submit"
                  className={`absolute right-2 top-1/2 transform -translate-y-1/2 ${glp1Mode ? 'bg-gold-500 hover:bg-gold-600' : 'bg-sky-600 hover:bg-sky-700'} text-white px-6 py-2 rounded-full transition-colors`}
                >
                  <FiSearch className="w-5 h-5" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {categories.map((category) => (
                <button
                  key={category.name}
                  onClick={() => handleCategoryChange(category.name)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                    activeCategory === category.name
                      ? filterActiveColor
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {category.displayName}
                  {category.name !== 'ALL' && categoryCounts[category.name] > 0 && (
                    <span className="ml-1 text-xs opacity-75">
                      ({categoryCounts[category.name]})
                    </span>
                  )}
                </button>
              ))}
            </div>

            {user?.role === 'ADMIN' && (
              <Link
                to="/blog/new"
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-full text-sm font-medium transition-colors ml-4 shrink-0"
              >
                <FiPlus /> New Post
              </Link>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Featured Blogs */}
        {activeCategory === 'ALL' && featuredBlogs.length > 0 && (
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Featured Articles</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {featuredBlogs.map((blog) => (
                <Link
                  key={blog.id}
                  to={`/blog/${blog.slug}`}
                  className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all"
                >
                  <div className="aspect-video">
                    <img
                      src={blog.featuredImage || 'https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?w=600'}
                      alt={blog.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 ${getCategoryColor(blog.category)}`}>
                      {blog.categoryDisplayName}
                    </span>
                    <h3 className="text-xl font-bold mb-2 group-hover:text-sky-300 transition-colors">
                      {blog.title}
                    </h3>
                    <div className="flex items-center text-sm text-gray-300">
                      <FiClock className="mr-1" /> {blog.readTime} min read
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Blog Grid */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {activeCategory === 'ALL'
                ? (glp1Mode ? 'Latest GLP-1 Articles' : 'Latest Articles')
                : categories.find(c => c.name === activeCategory)?.displayName}
            </h2>
            <span className="text-gray-500">{blogs.length} articles</span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className={`animate-spin rounded-full h-12 w-12 border-4 ${glp1Mode ? 'border-gold-500' : 'border-sky-600'} border-t-transparent`}></div>
            </div>
          ) : fetchError ? (
            <div className="text-center py-20">
              <div className="text-6xl mb-4">⚠️</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Failed to load articles</h3>
              <p className="text-gray-500 mb-4">{fetchError}</p>
              <button
                onClick={fetchBlogs}
                className={`px-6 py-2 rounded-full text-white font-medium ${glp1Mode ? 'bg-gold-500 hover:bg-gold-600' : 'bg-sky-600 hover:bg-sky-700'} transition-colors`}
              >
                Try Again
              </button>
            </div>
          ) : blogs.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-6xl mb-4">📝</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No articles found</h3>
              <p className="text-gray-500">
                {searchQuery
                  ? 'Try a different search term'
                  : 'No articles in this category yet. Check back soon!'}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogs.map((blog) => (
                <article
                  key={blog.id}
                  className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition-shadow group"
                >
                  <Link to={`/blog/${blog.slug}`}>
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={blog.featuredImage || 'https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?w=600'}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  </Link>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(blog.category)}`}>
                        {blog.categoryDisplayName}
                      </span>
                      <span className="text-gray-400 text-sm">{formatDate(blog.createdAt)}</span>
                    </div>
                    <Link to={`/blog/${blog.slug}`}>
                      <h3 className={`text-lg font-bold text-gray-900 mb-2 transition-colors line-clamp-2 ${glp1Mode ? 'group-hover:text-gold-600' : 'group-hover:text-sky-600'}`}>
                        {blog.title}
                      </h3>
                    </Link>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {blog.summary}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {blog.authorImage ? (
                          <img
                            src={blog.authorImage}
                            alt={blog.authorName}
                            className="w-8 h-8 rounded-full mr-2"
                          />
                        ) : (
                          <div className={`w-8 h-8 rounded-full ${glp1Mode ? 'bg-gold-100' : 'bg-sky-100'} flex items-center justify-center mr-2`}>
                            <span className={`${glp1Mode ? 'text-gold-600' : 'text-sky-600'} text-sm font-medium`}>
                              {blog.authorName?.charAt(0) || 'F'}
                            </span>
                          </div>
                        )}
                        <span className="text-sm text-gray-700">{blog.authorName || 'Fitnession Team'}</span>
                      </div>
                      <div className="flex items-center text-gray-400 text-sm">
                        <FiClock className="mr-1" /> {blog.readTime} min
                        <span className="mx-2">|</span>
                        <FiEye className="mr-1" /> {blog.viewCount}
                      </div>
                    </div>
                    <Link
                      to={`/blog/${blog.slug}`}
                      className={`mt-4 inline-flex items-center font-medium text-sm transition-colors ${readMoreColor}`}
                    >
                      Read More <FiChevronRight className="ml-1" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* Categories Sidebar for Mobile */}
        {!glp1Mode && (
          <section className="mt-16 lg:hidden">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Categories</h3>
            <div className="bg-white rounded-xl shadow-md p-4">
              {categories.slice(1).map((category) => (
                <button
                  key={category.name}
                  onClick={() => handleCategoryChange(category.name)}
                  className="flex items-center justify-between w-full py-3 px-2 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
                >
                  <span className="text-gray-700">{category.displayName}</span>
                  <span className="text-gray-400 text-sm">
                    {categoryCounts[category.name] || 0}
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Newsletter Section */}
        <section className={`mt-16 ${newsletterGradient} rounded-2xl p-8 md:p-12 text-white text-center`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            {glp1Mode ? 'Stay Informed on GLP-1 Treatments' : 'Stay Updated with Health Tips'}
          </h2>
          <p className={`${glp1Mode ? 'text-gray-300' : 'text-sky-100'} mb-6 max-w-xl mx-auto`}>
            {glp1Mode
              ? 'Get the latest GLP-1 research, patient stories, and medical updates delivered to your inbox.'
              : 'Subscribe to our newsletter and get the latest articles on weight loss, nutrition, and wellness.'}
          </p>
          <form className="max-w-md mx-auto flex gap-3">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-white/30"
            />
            <button
              type="submit"
              className={`${glp1Mode ? 'bg-gold-500 hover:bg-gold-600' : 'bg-orange-500 hover:bg-orange-600'} px-6 py-3 rounded-lg font-medium transition-colors`}
            >
              Subscribe
            </button>
          </form>
        </section>
      </div>
    </div>
  );
};

export default Blog;
